export const BASE_CERTIFIED_PER_PAGE_USD = 24.5;

// Standard tier per-word rates (PROD-2026-05-18-002). Mirror the
// marketing-site constants so both checkouts charge the same.
export const STANDARD_PER_WORD_USD = 0.1;
export const STANDARD_EXPEDITED_PER_WORD_USD = 0.04;
export const STANDARD_DESKTOP_PUB_PER_WORD_USD = 0.04;
export const STANDARD_PROOFREADING_PER_WORD_USD = 0.05;

// Standard tier floors (PROD-2026-05-18-002 + PROD-2026-05-18-003).
// Translation: 1 standard page (250 words) at $0.10/word.
// Add-ons: flat $10 minimum each.
export const STANDARD_TRANSLATION_MIN_USD = 25;
export const STANDARD_ADDON_MIN_USD = 10;

// Certified flat-fee add-ons. Notarization matches the canonical Stripe
// price ($19.95) per PROD-2026-05-18-007. Mailed Hard Copy stays at $20.
export const NOTARIZATION_USD = 19.95;
export const MAILED_HARD_COPY_USD = 20;

export function fetchPerPagePrice(languages: string[]) {
  // Existing project uses per-language increments based on a lookup.
  // For V2 we keep it deterministic and simple.
  return BASE_CERTIFIED_PER_PAGE_USD * Math.max(1, languages.length);
}

/**
 * Standard translation cost: max($25, words * $0.10). Zero words = $0.
 * FUTURE: language-tier multiplier plugs in here.
 */
export function calculateStandardTranslationCost(wordCount: number): number {
  const words = Math.max(0, Number(wordCount) || 0);
  if (words <= 0) return 0;
  return Math.max(STANDARD_TRANSLATION_MIN_USD, words * STANDARD_PER_WORD_USD);
}

/**
 * Standard add-on cost: max($10, words * ratePerWord). Zero words = $0
 * (no parent translation, no add-on charge).
 */
export function calculateStandardAddonCost(
  ratePerWord: number,
  wordCount: number
): number {
  const words = Math.max(0, Number(wordCount) || 0);
  if (words <= 0) return 0;
  return Math.max(STANDARD_ADDON_MIN_USD, words * ratePerWord);
}

export function calculateOrderTotal(params: {
  transitionType: "standard" | "certified";
  include_expedited_turnaround?: string | boolean | number;
  totalPage: number;
  totalWords: number;
  targetLanguage: string[];
  isNotarized: boolean;
  isMailHardCopy: boolean;
  include_desktop_publishing?: boolean;
  include_proofreading?: boolean;
}) {
  const {
    transitionType,
    include_expedited_turnaround,
    totalPage,
    totalWords,
    targetLanguage,
    isNotarized,
    isMailHardCopy,
    include_desktop_publishing,
    include_proofreading,
  } = params;

  let total = 0;

  if (transitionType === "certified") {
    total = totalPage * fetchPerPagePrice(targetLanguage);
  } else {
    // Standard: per-word with $25 (1-page) floor.
    // Was: total = words * 0.10, with a $20 floor under 250 words.
    // Updated for PROD-003: floor raised to $25 (= 250 words * $0.10).
    total = calculateStandardTranslationCost(totalWords);
  }

  if (isNotarized) total += NOTARIZATION_USD;
  if (isMailHardCopy) total += MAILED_HARD_COPY_USD;

  if (transitionType === "standard") {
    // Standard add-ons: per-word with $10 floor each (PROD-002).
    // Was: rate * ceil(words/250), which silently double-billed
    // customers whose word count crossed any 250-word boundary by even
    // a single word.
    if (include_desktop_publishing) {
      total += calculateStandardAddonCost(STANDARD_DESKTOP_PUB_PER_WORD_USD, totalWords);
    }
    if (include_proofreading) {
      total += calculateStandardAddonCost(STANDARD_PROOFREADING_PER_WORD_USD, totalWords);
    }
  }

  const isExpedited =
    include_expedited_turnaround === true ||
    include_expedited_turnaround === 1 ||
    include_expedited_turnaround === "1";

  if (isExpedited) {
    if (transitionType === "standard") {
      total += calculateStandardAddonCost(STANDARD_EXPEDITED_PER_WORD_USD, totalWords);
    } else {
      // Certified expedited stays per-page (tier convention).
      const pages = Math.max(0, Number(totalPage || 0) || 0);
      total += 10 * pages;
    }
  }

  return total;
}
